import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import ArticleWithToC from '../components/Common/ArticleWithToC';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import Layout, { BACKGROUND } from '../components/Layouts/Layout';
import LearningCenterSeo from '../components/LearningCenter/LearningCenterSeo';
import HeadingWithForm from '../components/LearningCenter/PostHeaders/HeadingWithForm';
import GroupedContent from '../components/PageSections/GroupedContent';
import PageSection from '../components/PageSections/PageSection';
import FooterNav from '../components/Resources/FooterNav';
import { setUnlockedCookie, useCheckGatedCookie } from '../tools/hooks/useCheckGatedCookie';

FooterNav.propTypes = { onClick: PropTypes.func };
const WalledResource = ({ data, location, pageContext }) => {
  const content = data.post;
  const backgroundImage = content?.headingBackground?.file?.url;
  const typeName = content?.type?.typeName;
  const formData = {
    title: content.title,
    slug: content.slug,
    successCopy: content?.submissionSuccessMessage,
    fileUrl: content?.downloadableContent?.file?.url || '',
    contentIsDownloadable: content.contentIsDownloadable,
    heroImage: content.pageMetaData?.ogImage || content.heroImage,
    marketoFormId: content.marketoFormId,
  };

  let tableOfContents = [];
  if (content.walledContentTableOfContents && content.walledContentTableOfContents.content) {
    tableOfContents = content.walledContentTableOfContents.content.map((item) => {
      return {
        title: item.title,
        url: `#${item.sectionId}`,
        id: item.sectionId,
      };
    });
  }

  const [unlocked, setUnlocked] = useState(false);
  const scrollRef = useRef();
  const returnToTopRef = useRef();

  const onSuccess = async () => {
    if (!unlocked) {
      await setUnlocked(true);
      setUnlockedCookie();
      if (content.formSuccessRedirect) {
        window.location = content.formSuccessRedirect;
        return false;
      }
    }
    if (scrollRef && scrollRef.current) {
      smoothScrollToRef(scrollRef, null, 84);
    }
  };

  useCheckGatedCookie(setUnlocked, unlocked);

  let trackingPixelUrl =
    content.pageMetaData?.trackingPixelUrl ||
    'https://px.ads.linkedin.com/collect/?pid=209369&conversionId=6567314&fmt=gif';

  return (
    <Layout background={BACKGROUND.GREY} isDach={pageContext.isDach}>
      <div ref={returnToTopRef} className={'publish-date'} data-post-date={content.publishDate} />
      <HeadingWithForm
        id={'hero-section'}
        title={content.title}
        headingFontColor={content.headingFontColor}
        backgroundImageUrl={backgroundImage}
        formData={formData}
        superText={typeName}
        excerpt={content?.excerpt}
        onSuccess={onSuccess}
        trackingPixelUrl={trackingPixelUrl}
        uniqueTrackingPixel={content.uniqueTrackingPixel}
        downloadCtaLabel={content.downloadCtaLabel}
        //alreadySubmitted={unlocked}
      />
      {unlocked && content.walledContentBody && (
        <>
          <ScrollTo reference={scrollRef} />
          <PageSection slim>
            <ArticleWithToC
              onScrollToTop={onSuccess}
              id={'article'}
              style={{ borderTop: '1px solid black', paddingTop: '16px' }}
              location={location}
              article={content.walledContentBody}
              tableOfContents={tableOfContents}
            />
          </PageSection>
        </>
      )}
      {content.publicAdditionalResources && <GroupedContent section={content.publicAdditionalResources} />}
      <FooterNav returnRef={returnToTopRef} backLabel={'Return to Resources'} backLink={'/resources/'} />
    </Layout>
  );
};

export const Head = ({ data: { post }, location }) => (
  <>
    <LearningCenterSeo meta={post.pageMetaData} slug={location.pathname} />
    <meta name="zd-site-verification" content={process.env.GATSBY_ZENDESK_CRAWLER_KEY} />
  </>
);

export const pageQuery = graphql`
  query WalledResourceQuery($pageId: String!) {
    post: contentfulPageWalledResource(contentful_id: { eq: $pageId }) {
      ...WalledResource
    }
  }
`;

WalledResource.propTypes = {
  data: PropTypes.object,
  location: PropTypes.any,
};

export default WalledResource;
